// ============================================
// Login Page
// ============================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { FaUser, FaKey, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import './Auth.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    setLoading(true);
    try {
      await login(username, password);
      toast.success('เข้าสู่ระบบสำเร็จ');
      navigate('/');
    } catch (error) {
      const errorMessage = error.message || 'เข้าสู่ระบบไม่สำเร็จ';
      // Suppress console error for 429 errors
      if (error.is429 || errorMessage.includes('คำขอยังมากเกินไป')) {
        // Don't log 429 errors to console - they're expected during rate limiting
      } else {
        console.error('Login error:', error);
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <motion.div
        className="login-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Logo */}
        <div className="login-logo">
          <img 
            src="/images/assets/newstreaming_dark_bg--jXNTjCF.png"
            alt="Streaming logo" 
            className="login-logo-image"
          />
        </div>
        {/* Login Form */}
        <form onSubmit={handleSubmit} className="login-form">
          {/* Username Field */}
          <div className="login-input-group">
            <span className="login-input-icon">
              <FaUser />
            </span>
            <input
              type="text"
              className="login-input"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Password Field */}
          <div className="login-input-group">
            <span className="login-input-icon">
              <FaKey />
            </span>
            <input
              type={showPassword ? 'text' : 'password'}
              className="login-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <button
              type="button"
              className="login-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {/* Show Password Checkbox */}
          <div className="login-checkbox-group">
            <label className="login-checkbox-label">
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
                className="login-checkbox"
              />
              <span className="login-checkbox-custom"></span>
              <span className="login-checkbox-text">Show Password</span>
            </label>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            className="login-btn"
            disabled={loading}
          >
            {loading ? (
              <div className="spinner" style={{ width: '20px', height: '20px' }} />
            ) : (
              'Login'
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="login-divider">
          <span>or</span>
        </div>

        {/* Register Link */}
        <div className="login-register">
          <Link to="/register" className="login-register-link">
            สมัครสมาชิกเลย
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
